/**
 * Display helpers for rendering ValidatedSuggestion cards (Rule Compiler,
 * contract docs/RULE_COMPILER_CONTRACT.md §6).
 *
 * Kept pure and separately testable rather than inlined in the component —
 * matches the pattern of suggestedGoalToAnnotation.ts / templateHelpers.ts
 * for this codebase's suggestion-panel family.
 */

import type { GoalApplicability } from '@/types/api';
import type {
  RuleValidationEntry,
  RuleRelation,
  RuleValidationEvidence,
  ValidatedSuggestion,
} from '@/types/ruleCompiler';

/**
 * Generic, coach-safe label shown for a relation token this module does not
 * (yet) have hand-written copy for. NEVER the un-snake-cased token itself.
 */
const GENERIC_RELATION_LABEL = 'Movement check';

/**
 * Hand-written coach words for every relation token the compiler can emit
 * (the `RuleRelation` union, contract docs/RULE_COMPILER_CONTRACT.md §1).
 * Each phrase names what the check looks at, never the internal relation
 * name — docs/COACH_FACING_COPY_STANDARD.md §2 explicitly forbids relation
 * identifiers ("segment_tilt", "transverse_rotation", …) as coach-facing
 * copy, so a mechanical un-snake-case (the previous implementation) is not
 * an acceptable substitute for real copy.
 */
const RELATION_LABELS: Record<RuleRelation, string> = {
  joint_angle_limit: 'Joint angle limit',
  joint_angle_target: 'Joint angle target',
  segment_tilt: 'Body-part tilt',
  transverse_rotation: 'Facing direction',
  timing_order: 'Movement timing',
  separation_ratio: 'Distance apart',
  height_comparison: 'Height comparison',
  signed_projection: 'Position past a line',
  midline_crossing: "Crossing the body's centre line",
  bilateral_symmetry: 'Left/right balance',
};

/**
 * Humanize a relation string for display: `"joint_angle_limit"` ->
 * `"Joint angle limit"`.
 *
 * Previously this mechanically un-snake-cased whatever token the compiler
 * sent ("transverse_rotation" -> "Transverse rotation") so a new relation
 * would "display sensibly" without a frontend change. That is exactly the
 * leak docs/COACH_FACING_COPY_STANDARD.md §2 forbids: an internal relation
 * name is not coach language just because it has spaces and a capital
 * letter. The safe default is now the other direction — an unrecognised
 * relation (a new one the compiler starts emitting, or a typo) displays as
 * the generic, coach-safe `GENERIC_RELATION_LABEL` until someone writes its
 * real coach words in `RELATION_LABELS` above and ships that as a deliberate
 * copy change.
 */
export function humanizeRelation(relation: string): string {
  return RELATION_LABELS[relation as RuleRelation] ?? GENERIC_RELATION_LABEL;
}

/**
 * Human label for which LLM produced a compiled-rule suggestion (contract
 * `ValidatedSuggestion.compiler_provider`, added
 * backlog/bugs/rule-compiler-openai-only-and-leaks-vendor-errors.md
 * Defect 2). Quiet-provenance display -- deliverable 3 of
 * backlog/bugs/rule-compiler-refusal-text-leaks-internal-docs.md: shown as
 * small print on the suggestion card (see AnnotationSuggestionPanel.tsx's
 * CompiledRuleSuggestionCard), not a colored badge -- this is traceability
 * for the rare case a coach or support needs it, not something that should
 * compete visually with the rationale/status/confidence the card leads with.
 * Returns `null` for a missing/unrecognized value so the caller can skip
 * rendering entirely rather than show a raw wire token.
 */
export function providerLabel(provider: ValidatedSuggestion['compiler_provider']): string | null {
  switch (provider) {
    case 'openai':
      return 'OpenAI';
    case 'gemini':
      return 'Gemini';
    default:
      return null;
  }
}

/**
 * Coach-facing name for the take a suggestion's source came from, matching
 * the vocabulary already used in CalibrationTakesPanel's take-count badge
 * ("flawed" for bad, "disqualifying" for fail, "correct" for good).
 */
export function sourceClipLabel(sourceLabel: ValidatedSuggestion['source_label']): string {
  switch (sourceLabel) {
    case 'fail':
      return 'Disqualifying clip';
    case 'bad':
      return 'Flawed clip';
    case 'good':
      // Source takes are bad/fail per contract §3 — defensive fallback only.
      return 'clip';
    default:
      return 'source clip';
  }
}

// ---------------------------------------------------------------------------
// "Couldn't be checked" is not "didn't hold up" (audit item FE-7)
// ---------------------------------------------------------------------------

/**
 * Coach words for each `skip_reason` the backend can emit.
 *
 * The wire value is an internal string: a token, a colon, and an engineer's
 * explanation — e.g. *"no_counter_evidence: zero good takes available to
 * validate silence against (after self-reference exclusion, or the template has
 * none) — an empty good-takes pool would trivially satisfy 'silent on every
 * good take' without proving anything"*. None of that is coach-facing copy
 * (`docs/COACH_FACING_COPY_STANDARD.md` §2), and the `declaration_required:`
 * variant carries measured-vs-required confidence numbers a coach cannot act
 * on, so the raw string is NEVER rendered — matched to one of these sentences
 * instead.
 *
 * Matched as an ordered prefix/substring list rather than a `Record` lookup
 * because the backend's values are prefixes with a trailing explanation, and
 * two of them are not token-shaped at all (`rule_validation.py:408` starts
 * "take {id}: pose cache unreadable", `:417` "annotation has no '…' config").
 */
const SKIP_REASON_SENTENCES: ReadonlyArray<readonly [RegExp, string]> = [
  [
    /^no_source_take\b/,
    "We couldn't check this one, because it came from a mistake you typed rather than a clip of it happening.",
  ],
  [
    /^no_counter_evidence\b/,
    "We couldn't check this one, because there is no correct clip on this template to compare it against.",
  ],
  [
    /^declaration_required\b/,
    "We couldn't check this one until you tell us which side leads this movement.",
  ],
  [
    /pose cache unreadable/i,
    "We couldn't check this one, because we couldn't read the body position in that clip.",
  ],
  [
    /^malformed_candidate\b|annotation has no/i,
    "We couldn't check this one, because the check we built from your words came out incomplete.",
  ],
  // goal_frame_window.resolve_goal_frame_window's "misconfigured" family —
  // backlog/calibration-worker-inline-phase-match-ignores-span-and-range.md,
  // finding 3 asked for ONE sentence here; Batch 2 red-team Finding 6 found
  // that single sentence ("…doesn't have this part of the movement") was
  // being matched against THREE textually-distinct backend detail strings
  // that are three DIFFERENT coach-fixable problems — a span whose two ends
  // are listed backwards, and a span missing one of its two ends, are not
  // "this template doesn't have that part of the movement" at all; telling a
  // coach that could send them hunting for a phase to add/rename when the
  // phase is already there and fine. The two NEW, more specific entries
  // below are checked FIRST (array order = match order in
  // `skipReasonSentence`); the original catch-all stays last for the one
  // case it was always right about (a named phase/span-endpoint this
  // template genuinely does not have) and as a safety net for any detail
  // string not covered by the two specific patterns.
  [
    // "this check spans 'X' to 'Y', but 'Y' comes before 'X' in this
    // template's movement — the start and end must be in order"
    /comes before/i,
    "We couldn't check this one, because the two parts of the movement it compares are listed in the wrong order for this template — swap which part comes first, then it will check correctly.",
  ],
  [
    // "this check names a {start/end} phase ('{label}') but no {absent}
    // phase — a span needs both ends, or use a single phase"
    /names a .* but no\b/i,
    "We couldn't check this one, because it only names one end of a two-part comparison — add the missing end (or switch it to checking a single part of the movement), then it will check correctly.",
  ],
  [
    // "this check is set to the 'X' part of the movement, which this
    // template does not have…" (single phase) OR "this check spans 'X' to
    // 'Y', but X is not a phase this template has…" (a span endpoint this
    // template genuinely lacks).
    /^this check (is set to|spans)\b|^misconfigured\b/i,
    "We couldn't check this one, because it's set to a part of the movement this template doesn't have.",
  ],
];

/**
 * The sentence shown when a check could not be tested. Always coach-safe, and
 * never the wire string — an unrecognised value falls back to the generic
 * sentence rather than leaking.
 */
const GENERIC_SKIP_SENTENCE = "We couldn't check this one against your clips.";

export function skipReasonSentence(raw: string | null | undefined): string {
  if (!raw) return GENERIC_SKIP_SENTENCE;
  for (const [pattern, sentence] of SKIP_REASON_SENTENCES) {
    if (pattern.test(raw)) return sentence;
  }
  return GENERIC_SKIP_SENTENCE;
}

/**
 * One line about what happened when this check was tried against the coach's
 * clips — and whether it was tried AT ALL.
 *
 * `measured` is the field that matters. The card must say "it didn't hold up"
 * only when something was actually measured; when `measured` is false the
 * check was never tested, and telling a coach it "didn't hold up against your
 * calibration takes" is simply untrue (audit item `FE-7`: an inconclusive good
 * take, `fired: null` because the clip's body positions could not be read, was
 * reported to the coach as **"Passed"**).
 */
export interface ValidationEvidenceLine {
  /** True when at least one clip produced a real fired/didn't-fire answer. */
  measured: boolean;
  /** The coach-facing sentence. */
  sentence: string;
}

/** A per-clip entry counts as measured only when `fired` is a real boolean. */
function wasMeasured(entry: RuleValidationEntry | null | undefined): boolean {
  return typeof entry?.fired === 'boolean';
}

/**
 * Build the validation-evidence line for a card.
 *
 * Three shapes, three different answers — this is the whole point of the
 * rewrite (plan §"PR-8" → "the three `FE-7` shapes each render distinct
 * wording"):
 *
 *  1. **Measured.** At least one clip answered. e.g. "Passed on Good 1 — fires
 *     on your Flawed clip". A clip whose `fired` came back `null` is named as
 *     one we couldn't check, NEVER folded into "Passed".
 *  2. **Never tested.** The whole `validation` dict is a bare
 *     `{skip_reason: …}`, or every entry in it is inconclusive. Renders the
 *     reason, in coach words, with `measured: false`.
 *  3. **No validation at all** (`validation: null`, e.g. a `not_expressible`
 *     suggestion). Returns `null` — there is nothing to say.
 */
export function buildValidationEvidence(
  suggestion: ValidatedSuggestion,
): ValidationEvidenceLine | null {
  const validation: RuleValidationEvidence | null = suggestion.validation;
  if (!validation) return null;

  const goods = validation.goods ?? {};
  const goodKeys = Object.keys(goods);
  const source = validation.source ?? null;

  const passedLabels: string[] = [];
  const firedLabels: string[] = [];
  const uncheckedLabels: string[] = [];
  // The first reason we find, so a "never tested" line can name a cause
  // instead of shrugging. Order: the whole-dict reason, then the source clip,
  // then the good clips in insertion order.
  let firstSkipReason: string | null = validation.skip_reason ?? null;

  goodKeys.forEach((key, i) => {
    const entry = goods[key];
    const label = `Good ${i + 1}`;
    if (!wasMeasured(entry)) {
      uncheckedLabels.push(label);
      if (!firstSkipReason) firstSkipReason = entry?.skip_reason ?? null;
      return;
    }
    if (entry.fired) firedLabels.push(label);
    else passedLabels.push(label);
  });

  const sourceMeasured = wasMeasured(source);
  if (source && !sourceMeasured && !firstSkipReason) {
    firstSkipReason = source.skip_reason ?? null;
  }

  // Shape 2 — nothing was measured anywhere.
  if (passedLabels.length === 0 && firedLabels.length === 0 && !sourceMeasured) {
    return { measured: false, sentence: skipReasonSentence(firstSkipReason) };
  }

  // Shape 1 — at least one real measurement. Say what held, what fired, and
  // what we couldn't check, in that order.
  const parts: string[] = [];
  if (passedLabels.length > 0) {
    parts.push(`Passed on ${passedLabels.join(', ')}`);
  }
  if (firedLabels.length > 0) {
    parts.push(`fired on ${firedLabels.join(', ')} (should have passed)`);
  }
  if (uncheckedLabels.length > 0) {
    parts.push(`couldn't be checked on ${uncheckedLabels.join(', ')}`);
  }
  if (source) {
    const clip = sourceClipLabel(suggestion.source_label);
    if (sourceMeasured) {
      parts.push(source.fired ? `fires on your ${clip}` : `did not fire on your ${clip}`);
    } else {
      parts.push(`couldn't be checked on your ${clip}`);
    }
  }

  return { measured: true, sentence: parts.join(' — ') };
}

// ---------------------------------------------------------------------------
// Can this check be measured on this demo? (PR-8)
// ---------------------------------------------------------------------------

/**
 * The short label beside a suggestion saying whether it can be measured on the
 * coach's own demo — the authoring-time answer to the question a coach
 * otherwise only gets from a report that quietly left the check out.
 *
 * Keyed on the attached notice's `kind` (a closed set this repo owns, mirrored
 * from `backend/app/config/coach_notices.yaml`) rather than on
 * `applicability.reason_kind`, which is an open backend vocabulary. An
 * unrecognised reason still gets the verdict-level label, so a new backend
 * reason can never print a raw token.
 *
 * `cannot_assess` is its own answer and never reads as either of the other
 * two: the camera view is auto-detected only behind a default-OFF flag, so
 * "we don't know" is the ordinary case on a template whose coach declared no
 * camera angle, and calling that "can be measured" is the specific lie the
 * precheck exists to stop.
 */
const APPLICABILITY_LABELS_BY_VERDICT: Record<GoalApplicability['verdict'], string> = {
  can_measure: 'Can be measured on your demo',
  cannot_measure: "Can't be measured on your demo",
  cannot_assess: "We can't tell from this demo — set the camera angle when you save",
};

const APPLICABILITY_LABELS_BY_NOTICE_KIND: Record<string, string> = {
  cannot_measure_camera_angle: "Can't be measured from this camera angle",
  cannot_measure_no_reference: "Can't be measured — nothing in your demo to compare against",
  cannot_assess_camera_unknown:
    "We can't tell from this demo — set the camera angle when you save",
  declaration_required: "Can't be measured until you say which side leads",
};

export function applicabilityLabel(applicability: GoalApplicability): string {
  const byKind = applicability.notice
    ? APPLICABILITY_LABELS_BY_NOTICE_KIND[applicability.notice.kind]
    : undefined;
  return byKind ?? APPLICABILITY_LABELS_BY_VERDICT[applicability.verdict];
}

/** Which badge colour the verdict reads as. `cannot_assess` is not a failure. */
export function applicabilityVariant(
  applicability: GoalApplicability,
): 'success' | 'warning' | 'default' {
  switch (applicability.verdict) {
    case 'can_measure':
      return 'success';
    case 'cannot_measure':
      return 'warning';
    default:
      return 'default';
  }
}

// ---------------------------------------------------------------------------
// Rebuild prompts (PR-8, owner decision D10)
// ---------------------------------------------------------------------------

/**
 * Shown when the server refuses a whole batch because the suggestions were
 * built by an older materialiser (409 `detail.kind === 'stale_suggestion'`).
 *
 * The server's own `detail.message` is preferred where it sends one, so the
 * coach reads the backend's words; this is the fallback for a 409 with no
 * message. WHAT: these were built before a change. WHY: they may not measure
 * what they say any more. HOW: rebuild, then review.
 */
export const STALE_SUGGESTIONS_PROMPT =
  'These suggestions were built before we changed how checks are built. ' +
  'Rebuild them, then review again.';

/**
 * Shown when `reasons_changed_since_compile` is true — the coach edited what
 * they wrote about a clip after these suggestions were compiled. The backend
 * has emitted this flag since the rule compiler shipped and nothing surfaced
 * it (audit item `FE-7`).
 */
export const REASONS_CHANGED_PROMPT =
  'You have changed what you wrote about a clip since these suggestions were ' +
  'built, so they still reflect your older wording. Rebuild them to review ' +
  'what you say now.';

/** The action on both prompts. Same words as the panel's own button. */
export const REBUILD_ACTION_LABEL = 'Rebuild suggestions';
